import { useEffect } from 'react'
import { observer, inject } from 'mobx-react'
import useMediaQuery from '@mui/material/useMediaQuery'

import { TooltipText } from '../../../components'
import { numberToDollars, withRouterProps } from '../../../utils'
import { MOBILE_VIEWPORT } from '../../../guided-toolbox/utils/breakpoints'
import {
  PageContainer,
  PageTitle,
  HeaderTop,
  BalanceGroup,
  BalanceLabel,
  TotalBalance,
  TotalDollarSign,
  AddAccountButton,
  AddAccountButtonCell,
  ProjectionsCell,
  HeaderSection,
} from './DashboardStyle'
import AccountsSectionCard from './AccountsSectionCard'
import DashboardProjections from './DashboardProjections'
import DashboardEmployerSponsoredAccount from './DashboardEmployerSponsoredAccount'
import DashboardStockOption from './DashboardStockOption'
import DashboardAccount from './DashboardAccount'
import DashboardSocialSecurity from './DashboardSocialSecurity'

const NO_EMPLOYER_ACCOUNTS_MESSAGE = "You haven't added any employer-related accounts yet."

const LUMP_SUM_NOTICE =
  '**We assume you have either invested the payout from this pension or used it for a purpose other than future retirement expenses. If you invested it, we assume you have updated the account balance where the money was invested or have added a new account.'

const rowKey = account => `dashboard-account-${account.type}-${account.id}`

const Dashboard = props => {
  const isMobile = useMediaQuery(MOBILE_VIEWPORT)

  const handleAddAccount = () => {
    const { navigate } = props
    navigate('/accounts/add')
  }

  const { store } = props
  const {
    features,
    config,
    employerAccounts,
    otherAccounts,
    accountsBalance,
    account: { managedAccount },
    stockOptionsOverview,
    allPensions,
    getAccounts,
    getAllPensions,
    getLimits,
  } = store

  const institutionalAccounts = employerAccounts.filter(account => account.type === 'Institutional')
  const sponsoredAccounts = employerAccounts.filter(
    account => account.type !== 'Institutional' && account.employerSponsoredAccount
  )
  const selfManagedAccounts = employerAccounts.filter(
    account => account.type !== 'Institutional' && !account.employerSponsoredAccount
  )

  const showStockOptionsSection =
    features.stockOptionsEnabled &&
    (stockOptionsOverview.hasVested || stockOptionsOverview.hasNotVested)

  const hasEmployerRows =
    institutionalAccounts.length + sponsoredAccounts.length + selfManagedAccounts.length > 0 ||
    showStockOptionsSection

  // Nothing has been added to project growth for, so the projections stay hidden.
  const hasAccounts = employerAccounts.length > 0 || otherAccounts?.length > 0

  const showProjections = hasAccounts && !config.isSpendown

  // Track lump-sum pensions so both their rows and the section can show the notice.
  const lumpSumPensionIds = new Set(
    allPensions
      .filter(pension => pension.paymentType === 'L' && pension.lumpsumreceived === 'Y')
      .map(pension => pension.id)
  )
  const showLumpSumNotice = selfManagedAccounts.some(account => lumpSumPensionIds.has(account.id))

  const managedAccountFootnote =
    hasEmployerRows &&
    features.managedAccountAvailable &&
    managedAccount &&
    config.template.managedAccountLabel
      ? `* ${config.template.managedAccountLabel}`
      : null

  useEffect(() => {
    getAccounts()
  }, [getAccounts])

  useEffect(() => {
    getLimits()
  }, [getLimits])

  useEffect(() => {
    getAllPensions().catch(err => console.error(err))
  }, [getAllPensions])

  return (
    <PageContainer data-no-mobile-gutter>
      <HeaderSection>
        <HeaderTop>
          <PageTitle>Your accounts</PageTitle>
          <AddAccountButtonCell>
            <AddAccountButton type='button' onClick={handleAddAccount}>
              + Add account
            </AddAccountButton>
          </AddAccountButtonCell>
        </HeaderTop>
        <BalanceGroup>
          <BalanceLabel>Today's balance</BalanceLabel>
          <TotalBalance>
            <TotalDollarSign>$</TotalDollarSign>
            {numberToDollars(accountsBalance, true)}
          </TotalBalance>
        </BalanceGroup>
        {/* Desktop shows the projections beside the balance; mobile gives them a
            full-width section of their own, below the add button. */}
        {showProjections && !isMobile && (
          <ProjectionsCell>
            <DashboardProjections />
          </ProjectionsCell>
        )}
      </HeaderSection>

      {showProjections && isMobile && <DashboardProjections />}

      {/* Keep the employer-related section visible when empty so it can show the add-account message. */}
      <AccountsSectionCard
        title='Employer-related accounts'
        tooltip={TooltipText.employerRelatedAccountsBalance()}
        emptyMessage={hasEmployerRows ? null : NO_EMPLOYER_ACCOUNTS_MESSAGE}
        footnote={managedAccountFootnote}
        notice={showLumpSumNotice ? LUMP_SUM_NOTICE : null}>
        {[...institutionalAccounts, ...sponsoredAccounts].map(account => (
          <DashboardEmployerSponsoredAccount
            account={account}
            key={rowKey(account)}
            id={rowKey(account)}
          />
        ))}
        {showStockOptionsSection && <DashboardStockOption />}
        {selfManagedAccounts.map(account => (
          <DashboardAccount
            account={account}
            showLumpSumNotice={lumpSumPensionIds.has(account.id)}
            key={rowKey(account)}
            id={rowKey(account)}
          />
        ))}
      </AccountsSectionCard>

      {otherAccounts?.length > 0 && (
        <AccountsSectionCard
          title='Other types of accounts'
          tooltip={TooltipText.otherRelatedAccountsBalance()}>
          {otherAccounts.map(account => (
            <DashboardAccount account={account} key={rowKey(account)} id={rowKey(account)} />
          ))}
        </AccountsSectionCard>
      )}

      <DashboardSocialSecurity />
    </PageContainer>
  )
}

export default withRouterProps(inject('store')(observer(Dashboard)))
